import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AccessLevel } from '@/__generated__/graphql';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useFeatureFlag } from '@/context/FeatureFlagProvider';
import { allSearchTypes, useFilterContext } from '@/context/FilterProvider';
import { useSearchFacets } from '@/context/SearchFacetsProvider';
import { useAccountMaterializationTypes } from '@/hooks/discovery/accountMaterializationTypes';
import { useAccountSearchResults } from '@/hooks/discovery/accountSearch';
import { useAccountSearchResultsV2 } from '@/hooks/discovery/accountSearchv2';
import { CUSTOM_MATERIALIZATION_TYPE } from '@/typings/dbt';
import { truthy } from '@/util/array';
import {
  buildCustomMaterializationAggregation,
  expandCustomMaterializationTypes,
} from '@/util/customMaterialization';
import { MAXIMUM_QUERY_LENGTH, QUERY_TOO_LONG_MESSAGE } from '@/util/input';
import { toSearchResourceType } from '@/util/resourceType';
import { INITIAL_PAGE_SIZE } from '../../project/resource/components/ResourceFilterTable';
import {
  AccountSearchResults,
  AccountSearchResultsParams,
} from './AccountSearchResultsList';

export const usePageTitleQuery = (): string => {
  const [searchParams] = useSearchParams();
  return searchParams.get('search') || '';
};

export const AccountSearchResultsContents = () => {
  const { filters } = useFilterContext();
  const { aggregations, setAggregations } = useSearchFacets();

  const pageSize = INITIAL_PAGE_SIZE;
  const [searchParams] = useSearchParams();
  const sideBySideSearchResults = useFeatureFlag('sideBySideSearchResults', false);
  const meta5507CatalogElasticsearch = useFeatureFlag(
    'meta5507CatalogElasticsearch',
    false,
  );
  /**
   * The actual query is the search term we want to filter by, ignoring all
   * selectors.
   */
  const searchInput = searchParams.get('search') || '';
  const isQueryTooLong = searchInput.length > MAXIMUM_QUERY_LENGTH;

  const { accountId } = useDbtCloudAccount();

  const [matTypesResult] = useAccountMaterializationTypes({ accountId });

  const matTypesAggregation = useMemo(() => {
    const types = matTypesResult.data?.account?.materializationTypes;
    if (!types) return null;
    return buildCustomMaterializationAggregation(types.map((t) => t.name));
  }, [matTypesResult.data]);

  // matTypesAggregation (useMemo) is available synchronously, but the
  // useEffect that seeds it into `aggregations` hasn't fired yet on the
  // same render cycle. Use it directly as a fallback to avoid a wasted
  // sentinel request on V1.
  const effectiveAggregations =
    aggregations ??
    (matTypesAggregation ? { dbtMaterializationTypes: matTypesAggregation } : null);

  const expandedMaterializationTypes = filters.materialization?.length
    ? expandCustomMaterializationTypes(filters.materialization, effectiveAggregations)
    : null;

  const customFilterPending =
    filters.materialization?.includes(CUSTOM_MATERIALIZATION_TYPE) &&
    !aggregations &&
    matTypesResult.fetching;

  const searchQuery = useAccountSearchResults({
    accountId,
    filter: {
      query: searchInput,
      fields: filters.searchFields?.length ? filters.searchFields : allSearchTypes,
      resourceTypes:
        filters.resourceType?.map(toSearchResourceType).filter(truthy) || [],
      tags: filters.tags || [],
      materializationTypes: expandedMaterializationTypes,
      modelingLayers: filters.modelingLayer?.length ? filters.modelingLayer : null,
      accessLevels: filters.access?.length ? (filters.access as AccessLevel[]) : null,
      assetTypes: [...(filters.platforms || [])],
      dbtEnvs: filters.environmentTypes || null,
      projectIds: filters.projects || null,
      warehouseEntityTypes: filters.warehouseAssetTypes || null,
      urns: null,
      environmentIds: null,
      uniqueIds: null,
    },
    pageSize: 25,
    pause:
      !accountId ||
      meta5507CatalogElasticsearch ||
      isQueryTooLong ||
      customFilterPending,
  });
  const searchQueryV2 = useAccountSearchResultsV2({
    accountId,
    filter: {
      query: searchInput,
      fields: filters.searchFields?.length ? filters.searchFields : allSearchTypes,
      resourceTypes:
        filters.resourceType?.map(toSearchResourceType).filter(truthy) ?? null,
      tags: filters.tags ?? null,
      materializationTypes: expandedMaterializationTypes,
      modelingLayers: filters.modelingLayer ?? null,
      accessLevels: (filters.access as AccessLevel[]) ?? null,
      assetTypes: filters.platforms ?? null,
      dbtEnvs: filters.environmentTypes ?? null,
      projectIds: filters.projects ?? null,
      warehouseEntityTypes: filters.warehouseAssetTypes ?? null,
      urns: null,
      environmentIds: null,
      uniqueIds: null,
    },
    pageSize: 25,
    pause:
      !accountId ||
      (!sideBySideSearchResults && !meta5507CatalogElasticsearch) ||
      isQueryTooLong ||
      customFilterPending,
  });

  const title = usePageTitleQuery();

  const resultsProps = useMemo((): AccountSearchResultsParams | null => {
    const pageInfo = {
      hasMoreResults:
        (searchQuery as any)?.hasMoreResults ||
        (searchQuery as any)?.pageInfo?.hasNextPage,
      totalCount:
        (searchQuery as any)?.totalCount || (searchQuery as any).pageInfo?.totalCount,
      isError: !!(searchQuery as any)?.error,
    };
    return {
      ...searchQuery,
      ...pageInfo,
      pageSize,
      query: searchInput,
      title,
      skeleton: searchQuery.isInitialLoad,
    };
  }, [searchQuery, pageSize, searchInput, title]);

  const resultsPropsV2 = useMemo((): AccountSearchResultsParams | null => {
    const pageInfo = {
      hasMoreResults:
        (searchQueryV2 as any)?.hasMoreResults ||
        (searchQueryV2 as any)?.pageInfo?.hasNextPage,
      totalCount:
        (searchQueryV2 as any)?.totalCount ||
        (searchQueryV2 as any).pageInfo?.totalCount,
      isError: !!(searchQueryV2 as any)?.error,
    };

    return {
      ...searchQueryV2,
      ...pageInfo,
      pageSize,
      query: searchInput,
      title,
      skeleton: searchQueryV2.isInitialLoad,
    };
  }, [searchQueryV2, pageSize, searchInput, title]);

  useEffect(() => {
    if (searchQueryV2.aggregations) {
      setAggregations(searchQueryV2.aggregations);
    }
  }, [searchQueryV2, setAggregations]);

  // When searchv2 is not active (meta5507 off), V1 search has no aggregation
  // support. Seed aggregations from the account.materializationTypes endpoint
  // so expandCustomMaterializationTypes can resolve "custom" to real type names.
  // When searchv2 IS active, its aggregations (with accurate counts) take
  // precedence via the effect above.
  useEffect(() => {
    if (!meta5507CatalogElasticsearch && matTypesAggregation && !aggregations) {
      setAggregations({ dbtMaterializationTypes: matTypesAggregation });
    }
  }, [
    meta5507CatalogElasticsearch,
    matTypesAggregation,
    aggregations,
    setAggregations,
  ]);

  const singleResultsProps = meta5507CatalogElasticsearch
    ? resultsPropsV2
    : resultsProps;

  if (isQueryTooLong) {
    return <div className="p-4 text-sm text-fgDanger">{QUERY_TOO_LONG_MESSAGE}</div>;
  }

  return sideBySideSearchResults && !meta5507CatalogElasticsearch ? (
    <div className="flex h-full gap-4">
      <div className="min-w-0 flex-1">
        <h3 className="mb-4 text-lg font-semibold text-gray-700">Postgres</h3>
        {resultsProps && (
          <AccountSearchResults {...resultsProps} title={resultsProps?.title || ''} />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="mb-4 text-lg font-semibold text-gray-700">Elasticsearch</h3>
        {resultsPropsV2 && (
          <AccountSearchResults
            {...resultsPropsV2}
            title={resultsPropsV2?.title || ''}
          />
        )}
      </div>
    </div>
  ) : (
    <div>
      {singleResultsProps && (
        <AccountSearchResults
          {...singleResultsProps}
          title={singleResultsProps?.title || ''}
        />
      )}
    </div>
  );
};
